// ── API client module ──

const TOKEN_KEY = 'tipatask_token';

// Set once any session (localStorage JWT OR the httpOnly Google cookie) is
// confirmed live — see auth.js's checkAuth()/verifySession(). Distinguishes a
// genuinely-expired session (show "Session expired") from a first-time visitor
// who was never signed in (go straight to the sign-in page, C1249). Cleared
// only on explicit logout() — NOT in clearToken(), since _triggerReauth() below
// calls clearToken() first and would otherwise erase the evidence it needs.
const HAD_SESSION_KEY = 'tt_had_session';

// In-memory cache so a single setAuthToken() update is visible to all
// subsequent fetch calls within the same page lifetime.
let _token = localStorage.getItem(TOKEN_KEY);

function getToken() { return _token; }

function setAuthToken(jwt) {
  _token = jwt || null;
  if (jwt) {
    localStorage.setItem(TOKEN_KEY, jwt);
    localStorage.setItem(HAD_SESSION_KEY, '1');
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

// Back-compat alias (was a dead export — now wired)
function setToken(t) { setAuthToken(t); }

function clearToken() {
  _token = null;
  localStorage.removeItem(TOKEN_KEY);
}

// Pending re-auth: all 401 callers await this promise until the user re-authenticates.
let _reauthPromise = null;

function _triggerReauth() {
  if (_reauthPromise) return _reauthPromise;

  // No prior session ever confirmed on this browser (first-time visitor, or one
  // who explicitly logged out) — a 401 here is expected, not an expiry. Skip the
  // "Session expired" modal and let the caller's existing 401 handling redirect
  // straight to the sign-in page (C1249).
  if (!localStorage.getItem(HAD_SESSION_KEY)) {
    clearToken();
    return Promise.reject(new Error('no prior session'));
  }

  clearToken();
  _reauthPromise = new Promise((resolve, reject) => {
    const onRefreshed = () => { cleanup(); resolve(); };
    const onCancelled = () => { cleanup(); reject(new Error('auth:cancelled')); };
    const cleanup = () => {
      window.removeEventListener('auth:refreshed', onRefreshed);
      window.removeEventListener('auth:cancelled', onCancelled);
      _reauthPromise = null;
    };
    window.addEventListener('auth:refreshed', onRefreshed, { once: true });
    window.addEventListener('auth:cancelled', onCancelled, { once: true });
    window.dispatchEvent(new CustomEvent('auth:expired', { detail: { source: 'http' } }));
  });
  return _reauthPromise;
}

async function apiFetch(path, options = {}) {
  const url = '/api' + path;
  const headers = { ...options.headers };

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;

  const { _authRetry, _silent, ...fetchOpts } = options;
  const res = await fetch(url, { ...fetchOpts, headers });

  // Silent probe (e.g. session re-check on the login screen): never pop the
  // reauth modal or redirect — just report failure to the caller.
  if (res.status === 401 && _silent) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || 'Unauthorized');
    err.status = 401;
    err.body = body;
    throw err;
  }

  if (res.status === 401 && !_authRetry) {
    try {
      await _triggerReauth();
      return apiFetch(path, { ...options, _authRetry: true });
    } catch {
      window.location.hash = '#/login';
      throw new Error('Unauthorized');
    }
  }

  if (res.status === 401) {
    clearToken();
    window.location.hash = '#/login';
    throw new Error('Unauthorized');
  }

  if (res.status === 204) return null;

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || res.statusText);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return res.json();
}

const api = {
  get:    (path)       => apiFetch(path),
  post:   (path, body) => apiFetch(path, { method: 'POST',   body: JSON.stringify(body) }),
  patch:  (path, body) => apiFetch(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  put:    (path, body) => apiFetch(path, { method: 'PUT',    body: JSON.stringify(body) }),
  delete: (path)       => apiFetch(path, { method: 'DELETE' }),
};

export { api, apiFetch, getToken, setToken, setAuthToken, clearToken, TOKEN_KEY, HAD_SESSION_KEY };
